// services.js — the "Kinetic Capabilities" section: a line-masked statement,
// a marquee band that skews and accelerates with scroll velocity, and four
// full-bleed rows whose hover choreography (accent sweep, type flip, thumbnail
// wipe, body expand) is handled entirely in CSS.
//
// This file drives only the two things CSS can't: the headline line-mask reveal
// and the velocity-reactive marquee. Reduced-motion clears both to static.
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function initServices() {
  const section = document.querySelector('[data-services]');
  if (!section) return;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = gsap.utils.clamp;

  // ── headline line-mask reveal ─────────────────────────────────────────
  const lines = [...section.querySelectorAll('[data-service-title] .line > span')];
  if (lines.length) {
    if (reduced) {
      gsap.set(lines, { yPercent: 0 });
    } else {
      gsap.set(lines, { yPercent: 110 });
      ScrollTrigger.create({
        trigger: '.serviceHead',
        start: 'top 78%',
        once: true,
        onEnter: () => gsap.to(lines, { yPercent: 0, duration: 1, stagger: 0.1, ease: 'power4.out' }),
      });
    }
  }

  // ── kinetic marquee — base drift + scroll-velocity boost & skew ───────
  const track = section.querySelector('[data-service-marquee-track]');
  if (track && !reduced) {
    let half = track.scrollWidth / 2;   // one full copy of the string
    const measure = () => { half = track.scrollWidth / 2; };
    window.addEventListener('resize', measure);
    window.addEventListener('load', measure);

    let x = 0, skew = 0, vel = 0, lastY = window.scrollY;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      vel = y - lastY;
      lastY = y;
    }, { passive: true });

    let running = false;
    const BASE = 0.6;   // px/frame idle drift, leftwards
    const tick = () => {
      vel *= 0.9;                                   // decay between scroll events
      x -= BASE + Math.min(Math.abs(vel) * 0.4, 34); // faster while scrolling
      if (half > 0) { while (x <= -half) x += half; } // seamless wrap
      const targetSkew = clamp(-14, 14, vel * -0.45);
      skew += (targetSkew - skew) * 0.1;
      track.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0) skewX(${skew.toFixed(2)}deg)`;
    };

    // only spin the loop while the band is on screen
    ScrollTrigger.create({
      trigger: section, start: 'top bottom', end: 'bottom top',
      onToggle: (self) => {
        if (self.isActive && !running) { running = true; gsap.ticker.add(tick); }
        else if (!self.isActive && running) { running = false; gsap.ticker.remove(tick); }
      },
    });
    measure();
  }

  window.addEventListener('load', () => ScrollTrigger.refresh());

  // dev handle (matches window.__about / __folio used elsewhere)
  window.__services = { section, ScrollTrigger, gsap };
}
